import { DigilaAnalyticsContext } from '@digila-ui/analytics/analytics'
import { getUserInfo, updateGuardianKycInfo, verifyAuthCode } from '@digila-ui/api/endpoints'
import { ApplicationContext } from '@digila-ui/state/ApplicationContext'
import { StepType } from '@digila-ui/types'
import {
  getAndStoreInputParams,
  getAuthenticationMode,
  getDigilaSourceChannel,
  getDigiliUrl,
  getUserInfoErrorProperties,
} from '@digila-ui/utils/helpers'
import { assertValue } from '@shared/helpers'
import { AppId, AppName, DigiliFlow } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'
import { StepErrorBoundary } from '@ui-common/components/StepErrorBoundary'
import { AuthenticateStep } from '@ui-common/steps/AuthenticateStep'
import { GetUserInfoStep } from '@ui-common/steps/GetUserInfoStep/GetUserInfoStep'
import { InitializeStep } from '@ui-common/steps/InitializeStep'
import { lazy, use } from 'react'
import { useIntl } from 'react-intl'

import { ServiceSelectionContext } from '../state/ServiceSelectionContext'
import { SERVICE_WIZARD_STATE } from '../state/wizardMachine'
import { EntranceStep } from './steps/EntranceStep/EntranceStep'
import { GateKind } from './steps/GateStep/types'
import { useWizardLogic } from './useWizardLogic'

const KycWizard = lazy(() => import('./common/KycWizardProvider'))
const ServiceWizard = lazy(() => import('./ServiceWizard/ServiceWizard'))
const GateStep = lazy(() => import('./steps/GateStep/GateStep'))
const EndStep = lazy(() => import('./steps/EndStep/EndStep'))
const MinorStep = lazy(() => import('./steps/MinorStep/MinorStep'))

export const Wizard = () => {
  const intl = useIntl()
  const { analyticsService } = use(DigilaAnalyticsContext)
  const logic = useWizardLogic()
  const {
    step,
    authToken,
    user: guardian,
    exit,
    onInitialized,
    setToken,
    setUser,
    setMinorUser,
    onGuardianKycDone,
    onDigiliRequired,
    result,
    minorUser,
    wizardData,
    params,
  } = logic
  const sourceChannel = getDigilaSourceChannel()

  const stepComponentMap: Partial<Record<StepType | 'ServiceWizard', () => React.ReactNode>> = {
    [StepType.Initialize]: () => (
      <InitializeStep
        actions={{ onInitialized, getAndStoreInputParams, onInitializedExit: () => exit() }}
        appName={AppName.DigiLa}
        shouldGetConfig={true}
        testId={StepType.Initialize}
      />
    ),
    [StepType.Entrance]: () => <EntranceStep />,
    [StepType.Authenticate]: () => (
      <AuthenticateStep
        onExit={exit}
        onAuthenticated={setToken}
        authMode={getAuthenticationMode(sourceChannel)}
        verifyAuthCodeCallback={verifyAuthCode}
        testId={StepType.Authenticate}
      />
    ),
    [StepType.GetUserInfo]: () => (
      <GetUserInfoStep
        onAbort={exit}
        onGetUserInfoDone={setUser}
        getUserInfo={getUserInfo}
        getErrorProperties={getUserInfoErrorProperties(intl)}
        testId={StepType.GetUserInfo}
      />
    ),
    [StepType.MinorSsn]: () => <MinorStep setMinorUser={setMinorUser} onDigiliRequired={onDigiliRequired} />,
    [StepType.KycGate]: () => <GateStep gateKind={GateKind.Kyc} />,
    [StepType.DigipaRequiredGate]: () => (
      <GateStep
        gateKind={GateKind.Digipa}
        targetUrl={getDigiliUrl(assertValue(params, 'logic.params').DIGILI_BASE_URL, sourceChannel, DigiliFlow.DigiPa)}
      />
    ),
    [StepType.DigiliRequiredGate]: () => (
      <GateStep
        gateKind={GateKind.Digili}
        targetUrl={getDigiliUrl(assertValue(params, 'logic.params').DIGILI_BASE_URL, sourceChannel, DigiliFlow.DigiLi)}
      />
    ),
    [StepType.GuardianKyc]: () => (
      <KycWizard
        kycKind={StepType.GuardianKyc}
        subject={guardian}
        updateKycInfo={updateGuardianKycInfo}
        onAbort={exit}
        onFinished={onGuardianKycDone}
      />
    ),
    [SERVICE_WIZARD_STATE]: () => <ServiceWizard />,
    [StepType.End]: () => <EndStep result={result} />,
  }

  return (
    <StepErrorBoundary onExitClick={() => exit(true)}>
      <ApplicationContext value={logic}>
        <ServiceSelectionContext
          value={{
            selectedServices: wizardData.selectedServices || [],
            selectedAuthorizations: wizardData.selectedAuthorizations || [],
          }}
        >
          <CommonWizardWrapper
            stepComponentMap={stepComponentMap}
            authToken={authToken ?? null}
            step={step}
            appId={AppId.DigiLa}
            analyticsService={analyticsService}
            onLogout={exit}
            userContext={{ user: guardian, secondaryUser: minorUser ?? undefined }}
          />
        </ServiceSelectionContext>
      </ApplicationContext>
    </StepErrorBoundary>
  )
}
